import { apiRequest, type ApiResponse } from '../services/api';
import type {
  CheckInInput,
  ExecutionApplyInput,
  ExecutionApplyResult,
  ExecutionDraft,
  ExecutionKind,
  ExecutionNode,
  ExecutionParent,
  ExecutionReview,
  ExecutionSnapshot,
  NodeInput,
  ObjectiveCheckIn,
  PrimaryMeasure,
} from './model';

export class ExecutionApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public currentRevision?: number
  ) {
    super(message);
    this.name = 'ExecutionApiError';
  }
}
export function unwrapExecutionResponse<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === undefined)
    throw new ExecutionApiError(
      response.error || 'Execution data could not be loaded. Your edits are retained.',
      response.status || 0,
      response.code,
      response.currentRevision
    );
  return response.data;
}
async function request<T>(path: string, method = 'GET', data?: unknown): Promise<T> {
  return unwrapExecutionResponse(
    await apiRequest<T>(path, {
      method,
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    })
  );
}
const base = (companyId: string) => `/execution/${companyId}`;
export const executionApi = {
  workspace: (companyId: string) => request<ExecutionSnapshot>(base(companyId)),
  saveMeasure: (
    companyId: string,
    objectiveId: string,
    primaryMeasure: PrimaryMeasure,
    expectedRevision: number,
    reviewIntervalDays: number
  ) =>
    request(`${base(companyId)}/objectives/${objectiveId}/measure`, 'PUT', {
      primaryMeasure,
      expectedRevision,
      reviewIntervalDays,
    }),
  checkIns: (companyId: string, objectiveId: string) =>
    request<ObjectiveCheckIn[]>(`${base(companyId)}/objectives/${objectiveId}/check-ins`),
  checkIn: (companyId: string, objectiveId: string, input: CheckInInput) =>
    request<ObjectiveCheckIn>(
      `${base(companyId)}/objectives/${objectiveId}/check-ins`,
      'POST',
      input
    ),
  alignProject: (companyId: string, projectId: string, strategyId: string) =>
    request(`${base(companyId)}/projects/${projectId}/alignment`, 'PATCH', { strategyId }),
  alignTask: (companyId: string, taskId: string, projectId: string) =>
    request(`${base(companyId)}/tasks/${taskId}/alignment`, 'PATCH', { projectId }),
  createNode: (
    companyId: string,
    kind: 'strategy' | 'project' | 'task',
    parent: ExecutionParent | null,
    input: NodeInput
  ) => request<ExecutionNode>(`${base(companyId)}/nodes`, 'POST', { kind, parent, ...input }),
  updateNode: (companyId: string, type: ExecutionKind, id: string, input: Partial<NodeInput>) =>
    request<ExecutionNode>(`${base(companyId)}/nodes/${type}/${id}`, 'PATCH', input),
  deleteNode: (companyId: string, type: 'project' | 'task', id: string) =>
    request<{ deleted: string[] }>(`${base(companyId)}/nodes/${type}/${id}`, 'DELETE'),
  review: (companyId: string, objectiveId: string, question?: string) =>
    request<ExecutionReview>(`${base(companyId)}/ai/review`, 'POST', {
      objectiveId,
      ...(question ? { question } : {}),
    }),
  draft: (
    companyId: string,
    objectiveId: string,
    parent: ExecutionParent,
    guidance?: string,
    documentText?: string
  ) =>
    request<ExecutionDraft>(`${base(companyId)}/ai/draft`, 'POST', {
      objectiveId,
      parent,
      ...(guidance ? { guidance } : {}),
      ...(documentText ? { documentText } : {}),
    }),
  apply: (companyId: string, input: ExecutionApplyInput) =>
    request<ExecutionApplyResult>(`${base(companyId)}/ai/apply`, 'POST', input),
};
